/**
 * Страница онбординга - сканирование QR для получения приватного ключа
 * Поддерживает deep link из URL: /onboarding?employee_id=xxx&token=yyy
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import QrScanner from 'qr-scanner'
import { useWorkerStore } from '../store/workerStore'
import { CryptoService } from '../services/cryptoService'
import { OnboardingApi, ApiClientError } from '../services/apiClient'
import { DebugLogger } from '../components/DebugLogger'
import './OnboardingPage.css'

export function OnboardingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isOnboarded, setEmployeeData, setEncryptedPrivateKey, setOnboarded } = useWorkerStore()
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string>('Готов к сканированию')
  const [isProcessing, setIsProcessing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const scannerRef = useRef<QrScanner | null>(null)
  const autoOnboardingStartedRef = useRef(false) // Для предотвращения повторных вызовов
  
  /**
   * Автоматический онбординг из deep link параметров
   */
  const handleAutoOnboarding = useCallback(async (employeeId: string, token: string) => {
    try {
      setIsProcessing(true)
      setIsLoading(true)
      setError(null)
      setStatus('Автоматическая регистрация...')

      // Валидация формата employee_id (UUID)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      if (!uuidRegex.test(employeeId)) {
        throw new Error('Невалидный формат ID сотрудника')
      }

      // Валидация токена
      if (typeof token !== 'string' || token.length < 10) {
        throw new Error('Невалидный формат токена')
      }

      // Проверяем подключение к интернету
      if (!navigator.onLine) {
        throw new Error('Нет подключения к интернету. Проверьте подключение и попробуйте снова.')
      }

      setStatus('Проверка токена...')

      // Отправляем запрос на backend для проверки токена
      console.log('[OnboardingPage] Calling OnboardingApi.verifyToken:', { employeeId, token: 'present' })
      const response = await OnboardingApi.verifyToken({
        token: token,
        employee_id: employeeId,
      })
      console.log('[OnboardingPage] ✅ Token verified', {
        employee_id: response.employee_id,
        role: response.role,
        full_name: response.full_name,
        has_private_key: !!response.private_key,
        has_totp_secret: !!response.totp_secret,
        short_id: response.short_id
      })

      setStatus('Сохранение данных...')

      // Шифруем приватный ключ для хранения
      const encryptedKey = CryptoService.encryptPrivateKey(
        response.private_key,
        response.employee_id
      )

      // Валидация role перед сохранением
      if (response.role !== 'worker' && response.role !== 'admin') {
        throw new Error('Невалидная роль сотрудника')
      }

      // Сохраняем все данные включая shortId и totpSecret
      setEmployeeData(
        response.employee_id,
        response.full_name,
        response.role,
        response.short_id
      )
      setEncryptedPrivateKey(encryptedKey)

      // Сохраняем TOTP secret в store для оффлайн генерации QR
      useWorkerStore.getState().setTotpSecret(response.totp_secret)

      setOnboarded(true)
      setStatus('✅ Регистрация успешно завершена!')

      // Перенаправляем на главную страницу через небольшую задержку
      setTimeout(() => {
        navigate('/', { replace: true })
      }, 1500)
    } catch (err) {
      console.error('[OnboardingPage] Auto-onboarding failed:', err)
      console.error('[OnboardingPage] Error type:', typeof err)
      console.error('[OnboardingPage] Error constructor:', err?.constructor?.name)
      if (err instanceof Error) {
        console.error('[OnboardingPage] Error message:', err.message)
        console.error('[OnboardingPage] Error stack:', err.stack)
      }
      
      setIsLoading(false)
      setIsProcessing(false)
      
      let message = 'Ошибка автоматической регистрации'
      
      if (err instanceof ApiClientError) {
        if (err.statusCode === 401) {
          message = 'Токен недействителен или уже использован'
        } else if (err.statusCode === 404) {
          message = 'Сотрудник не найден'
        } else if (err.statusCode === 400) {
          message = err.message || 'Неверный запрос'
        } else if (err.statusCode === 0 || !err.statusCode) {
          message = 'Не удалось подключиться к серверу. Проверьте подключение к интернету.'
        } else {
          message = err.message || `Ошибка сервера (${err.statusCode})`
        }
      } else if (err instanceof Error) {
        if (err.message === 'Load failed' || err.message.includes('Failed to fetch')) {
          message = 'Не удалось подключиться к серверу. Возможные причины:\n' +
                    '1. Нет подключения к интернету\n' +
                    '2. Проблема с конфигурацией приложения\n\n' +
                    'Попробуйте переустановить приложение или обратитесь к администратору.'
        } else {
          message = err.message
        }
      }
      
      setError(message + ' Используйте ручное сканирование QR-кода.')
      setStatus('Ошибка автоматической регистрации')
    }
  }, [setEmployeeData, setEncryptedPrivateKey, setOnboarded, navigate])
  
  // useEffect для автоматического онбординга при загрузке страницы
  useEffect(() => {
    console.log('[OnboardingPage] useEffect triggered', {
      isOnboarded,
      hasParams: searchParams.has('employee_id') && searchParams.has('token'),
      alreadyStarted: autoOnboardingStartedRef.current,
      url: window.location.href,
      localStorage: localStorage.getItem('pending_onboarding')
    })

    // Если уже прошёл онбординг, перенаправляем на главную страницу
    if (isOnboarded) {
      console.log('[OnboardingPage] Already onboarded, redirecting to home')
      navigate('/')
      return
    }

    // Проверяем наличие параметров deep link (из URL или localStorage)
    let employeeIdParam = searchParams.get('employee_id')
    let tokenParam = searchParams.get('token')
    
    console.log('[OnboardingPage] Initial params from URL:', {
      employeeIdParam,
      tokenParam: tokenParam ? 'present' : 'missing'
    })
    
    // Если параметров нет в URL, проверяем localStorage
    if (!employeeIdParam || !tokenParam) {
      const pendingOnboarding = localStorage.getItem('pending_onboarding')
      console.log('[OnboardingPage] Checking localStorage, found:', !!pendingOnboarding)
      
      if (pendingOnboarding) {
        try {
          const data = JSON.parse(pendingOnboarding)
          console.log('[OnboardingPage] Parsed localStorage data:', {
            hasEmployeeId: !!data.employee_id,
            hasToken: !!data.token,
            timestamp: new Date(data.timestamp).toISOString()
          })
          
          // Проверяем что данные не старше 1 часа
          const age = Date.now() - data.timestamp
          if (age < 60 * 60 * 1000) { // 1 час
            console.log('[OnboardingPage] Found valid pending onboarding in localStorage, age:', Math.round(age / 1000), 'seconds')
            employeeIdParam = data.employee_id
            tokenParam = data.token
          } else {
            console.log('[OnboardingPage] Pending onboarding expired (age:', Math.round(age / 1000), 'seconds), clearing')
            localStorage.removeItem('pending_onboarding')
          }
        } catch (e) {
          console.error('[OnboardingPage] Failed to parse pending_onboarding:', e)
          localStorage.removeItem('pending_onboarding')
        }
      }
    }

    // Проверяем установлено ли PWA
    const checkInstalled = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches
      const fullscreen = window.matchMedia('(display-mode: fullscreen)').matches
      const ios = (window.navigator as any).standalone === true
      const result = standalone || fullscreen || ios
      
      console.log('[OnboardingPage] PWA check:', {
        standalone,
        fullscreen,
        ios,
        result,
        userAgent: navigator.userAgent
      })
      
      return result
    }
    
    const installed = checkInstalled()

    console.log('[OnboardingPage] Decision point:', {
      employeeIdParam: employeeIdParam ? 'present' : 'missing',
      tokenParam: tokenParam ? 'present' : 'missing',
      installed,
      autoOnboardingStarted: autoOnboardingStartedRef.current
    })

    // Автоматический онбординг через deep link - ТОЛЬКО если PWA установлено
    if (employeeIdParam && tokenParam && installed && !autoOnboardingStartedRef.current) {
      console.log('[OnboardingPage] ✅ Starting auto-onboarding with params from:', 
        searchParams.has('employee_id') ? 'URL' : 'localStorage'
      )
      autoOnboardingStartedRef.current = true
      
      // Очищаем pending_onboarding после успешного использования
      localStorage.removeItem('pending_onboarding')
      
      handleAutoOnboarding(employeeIdParam, tokenParam)
    } else if (employeeIdParam && tokenParam && !installed) {
      // Если PWA не установлено но есть параметры - редиректим на install
      console.log('[OnboardingPage] Deep link detected but PWA not installed, redirecting to install')
      navigate(`/install${window.location.search}`, { replace: true })
      return
    } else {
      console.log('[OnboardingPage] ❌ No auto-onboarding - missing conditions:', {
        hasEmployeeId: !!employeeIdParam,
        hasToken: !!tokenParam,
        isInstalled: installed,
        alreadyStarted: autoOnboardingStartedRef.current
      })
    }
    
    return () => {
      // Очистка при размонтировании
      if (scannerRef.current) {
        scannerRef.current.destroy()
        scannerRef.current = null
      }
    }
  }, [isOnboarded, navigate, searchParams, handleAutoOnboarding])
  
  const startScanning = async () => {
    try {
      setError(null)
      setStatus('Запуск камеры...')
      
      if (!videoRef.current) {
        throw new Error('Video элемент не найден')
      }
      
      // Проверяем доступность камеры перед запуском
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Ваш браузер не поддерживает доступ к камере')
      }
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        // Останавливаем тестовый stream
        stream.getTracks().forEach(track => track.stop())
      } catch (mediaError: any) {
        // Обрабатываем ошибки доступа к камере
        if (mediaError.name === 'NotAllowedError' || mediaError.name === 'PermissionDeniedError') {
          throw new Error('NotAllowed')
        } else if (mediaError.name === 'NotFoundError' || mediaError.name === 'DevicesNotFoundError') {
          throw new Error('NotFound')
        } else if (mediaError.name === 'NotReadableError' || mediaError.name === 'TrackStartError') {
          throw new Error('NotReadable')
        } else if (mediaError.name === 'OverconstrainedError' || mediaError.name === 'ConstraintNotSatisfiedError') {
          throw new Error('Камера не поддерживает требуемые параметры')
        } else {
          throw mediaError
        }
      }
      
      // Создаём сканер
      const scanner = new QrScanner(
        videoRef.current,
        (result: QrScanner.ScanResult) => handleQRResult(result.data),
        {
          returnDetailedScanResult: true,
          maxScansPerSecond: 5,
          preferredCamera: 'environment', // Используем заднюю камеру
        }
      )
      
      await scanner.start()
      scannerRef.current = scanner
      setScanning(true)
      setStatus('Наведите камеру на QR-код')
      
      // Убеждаемся что видео видимо после запуска сканера
      // QrScanner может устанавливать свои стили, но мы переопределяем их
      setTimeout(() => {
        if (videoRef.current && scannerRef.current) {
          videoRef.current.style.display = 'block'
          videoRef.current.style.visibility = 'visible'
          videoRef.current.style.opacity = '1'
          videoRef.current.style.width = '100%'
          videoRef.current.style.height = '100%'
          videoRef.current.style.position = 'absolute'
          videoRef.current.style.top = '0'
          videoRef.current.style.left = '0'
          videoRef.current.style.zIndex = '1'
          videoRef.current.style.objectFit = 'cover'
        }
      }, 100)
    } catch (err) {
      let message = 'Ошибка запуска камеры'
      
      if (err instanceof Error) {
        // Определяем тип ошибки для более понятного сообщения
        if (err.message.includes('Permission') || err.message.includes('permission') || err.message === 'NotAllowed') {
          message = 'Необходимо разрешить доступ к камере. Проверьте настройки браузера.'
        } else if (err.message.includes('NotFound') || err.message.includes('not found')) {
          message = 'Камера не найдена. Убедитесь, что устройство имеет камеру.'
        } else if (err.message.includes('NotAllowed') || err.message.includes('not allowed')) {
          message = 'Доступ к камере запрещен. Разрешите доступ в настройках браузера.'
        } else if (err.message.includes('NotReadable') || err.message.includes('not readable')) {
          message = 'Камера занята другим приложением. Закройте другие приложения, использующие камеру.'
        } else if (err.message.includes('не поддерживает')) {
          message = err.message
        } else {
          message = err.message
        }
      }
      
      setError(message)
      setStatus('Ошибка')
      console.error('Failed to start scanner:', err)
    }
  }
  
  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.stop()
      scannerRef.current.destroy()
      scannerRef.current = null
    }
    setScanning(false)
    setStatus('Готов к сканированию')
    
    // Скрываем видео когда сканирование остановлено
    if (videoRef.current) {
      videoRef.current.style.display = 'none'
      // Останавливаем все треки видео
      if (videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream
        stream.getTracks().forEach(track => track.stop())
        videoRef.current.srcObject = null
      }
    }
  }
  
  const handleQRResult = async (qrData: string) => {
    // Защита от повторного сканирования
    if (isProcessing) {
      return
    }
    
    // Детальное логирование полученных данных
    console.log('[OnboardingPage] QR data received:', {
      data: qrData,
      type: typeof qrData,
      length: qrData?.length,
      trimmedLength: qrData?.trim?.()?.length,
      first100chars: qrData?.substring?.(0, 100)
    })
    
    // Проверка на пустую строку
    if (!qrData || typeof qrData !== 'string' || qrData.trim().length === 0) {
      console.error('[OnboardingPage] Invalid QR data - empty or not a string:', qrData)
      setError('QR-код пустой или невалидный')
      return
    }
    
    try {
      setIsProcessing(true)
      setError(null)
      setStatus('Обработка QR-кода...')
      
      // Останавливаем сканер сразу после начала обработки
      if (scannerRef.current) {
        scannerRef.current.stop()
      }
      
      let employee_id: string
      let token: string

      // Проверяем, является ли QR данными URL (deep link)
      if (qrData.startsWith('http://') || qrData.startsWith('https://')) {
        console.log('[OnboardingPage] QR contains URL, parsing as deep link')
        try {
          const url = new URL(qrData)
          const urlEmployeeId = url.searchParams.get('employee_id')
          const urlToken = url.searchParams.get('token')
          
          if (!urlEmployeeId || !urlToken) {
            throw new Error('URL не содержит необходимых параметров (employee_id, token)')
          }
          
          employee_id = urlEmployeeId
          token = urlToken
          console.log('[OnboardingPage] Deep link parsed successfully')
        } catch (urlError) {
          console.error('[OnboardingPage] Failed to parse URL:', urlError)
          throw new Error('Невалидный формат URL в QR-коде')
        }
      } else {
        // Парсим JSON из QR (старый формат, для обратной совместимости)
      let qrJson: { employee_id: string; token: string; expires_at?: string }
      try {
        console.log('[OnboardingPage] Attempting to parse QR JSON...')
        qrJson = JSON.parse(qrData.trim())
        console.log('[OnboardingPage] QR JSON parsed successfully:', {
          hasEmployeeId: !!qrJson?.employee_id,
          hasToken: !!qrJson?.token,
          hasExpiresAt: !!qrJson?.expires_at
        })
          
          if (!qrJson.employee_id || !qrJson.token) {
            throw new Error('QR-код не содержит необходимых данных')
          }
          
          employee_id = qrJson.employee_id
          token = qrJson.token
      } catch (parseError) {
        console.error('[OnboardingPage] JSON parse failed:', parseError)
        // Fallback: пытаемся использовать старый формат (для обратной совместимости)
        const oldData = CryptoService.decodeOnboardingQR(qrData)
        if (oldData) {
          // Старый формат - используем напрямую (для MVP совместимости)
          const encryptedKey = CryptoService.encryptPrivateKey(
            oldData.private_key,
            oldData.employee_id
          )
          setEmployeeData(
            oldData.employee_id,
            `Сотрудник ${oldData.employee_id.substring(0, 8)}`,
            oldData.role
          )
          setEncryptedPrivateKey(encryptedKey)
          setOnboarded(true)
          setStatus('Онбординг успешно завершён!')
          stopScanning()
          setTimeout(() => navigate('/'), 1000)
          return
        }
        throw new Error('Невалидный формат QR-кода')
      }
      }
      
      // Валидация формата employee_id (UUID)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      if (!uuidRegex.test(employee_id)) {
        throw new Error('Невалидный формат ID сотрудника в QR-коде')
      }
      
      // Валидация формата токена (должен быть непустой строкой)
      if (typeof token !== 'string' || token.length < 10) {
        throw new Error('Невалидный формат токена в QR-коде')
      }
      
      setStatus('Проверка токена...')
      setIsLoading(true)
      
      // Проверяем подключение к интернету перед отправкой запроса
      if (!navigator.onLine) {
        throw new Error('Нет подключения к интернету. Проверьте подключение и попробуйте снова.')
      }
      
      // Отправляем запрос на backend для проверки токена
      const response = await OnboardingApi.verifyToken({
        token: token,
        employee_id: employee_id,
      })
      
      setIsLoading(false)
      setStatus('Сохранение данных...')
      
      // Шифруем приватный ключ для хранения
      const encryptedKey = CryptoService.encryptPrivateKey(
        response.private_key,
        response.employee_id
      )
      
      // Валидация role перед сохранением
      if (response.role !== 'worker' && response.role !== 'admin') {
        throw new Error('Невалидная роль сотрудника')
      }
      
      // Сохраняем все данные включая shortId и totpSecret
      setEmployeeData(
        response.employee_id,
        response.full_name,
        response.role,
        response.short_id
      )
      setEncryptedPrivateKey(encryptedKey)
      
      // Сохраняем TOTP secret в store для оффлайн генерации QR
      useWorkerStore.getState().setTotpSecret(response.totp_secret)
      
      setOnboarded(true)
      
      setStatus('Онбординг успешно завершён!')
      
      // Останавливаем сканер полностью
      stopScanning()
      setIsProcessing(false)
      
      // Перенаправляем на главную страницу через небольшую задержку
      setTimeout(() => {
        navigate('/')
      }, 1000)
    } catch (err) {
      // Детальное логирование ошибки
      console.error('[OnboardingPage] Failed to process QR - Full error:', {
        error: err,
        errorType: typeof err,
        isError: err instanceof Error,
        isApiError: err instanceof ApiClientError,
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        name: err instanceof Error ? err.name : undefined,
        stringified: (() => {
          try {
            return JSON.stringify(err, Object.getOwnPropertyNames(err || {}), 2)
          } catch {
            return 'Could not stringify error'
          }
        })()
      })
      
      setIsLoading(false)
      setIsProcessing(false)
      
      let message = 'Ошибка обработки QR-кода'
      
      if (err instanceof ApiClientError) {
        console.log('[OnboardingPage] ApiClientError detected:', {
          statusCode: err.statusCode,
          message: err.message
        })
        if (err.statusCode === 401) {
          message = 'Токен недействителен или уже использован'
        } else if (err.statusCode === 404) {
          message = 'Сотрудник не найден'
        } else if (err.statusCode === 400) {
          message = err.message || 'Неверный запрос'
        } else if (err.statusCode === 0 || !err.statusCode) {
          message = 'Не удалось подключиться к серверу. Проверьте подключение к интернету.'
        } else {
          message = err.message || `Ошибка сервера (${err.statusCode})`
        }
      } else if (err instanceof Error) {
        console.log('[OnboardingPage] Standard Error:', err.message)
        message = err.message
      } else if (err && typeof err === 'object') {
        // Попытка извлечь сообщение из объекта ошибки
        const errObj = err as Record<string, unknown>
        if (typeof errObj.message === 'string') {
          message = errObj.message
        } else if (typeof errObj.error === 'string') {
          message = errObj.error
        }
        console.log('[OnboardingPage] Object error:', errObj)
      }
      
      setError(message)
      setStatus('Ошибка')
      
      // Останавливаем сканер при ошибке
      stopScanning()
    }
  }
  
  return (
    <div className="onboarding-page">
      <div className="onboarding-page__header">
        <h1 className="onboarding-page__title">Настройка приложения</h1>
        <p className="onboarding-page__subtitle">
          Отсканируйте QR-код от администратора для начала работы
        </p>
      </div>

      <div className="onboarding-page__content">
        {!scanning && !isProcessing && !error && (
          <div className="onboarding-page__instructions">
            <div className="onboarding-page__instructions-title">
              📋 Как начать:
            </div>
            <ul className="onboarding-page__instructions-list">
              <li className="onboarding-page__instructions-item">
                <span className="onboarding-page__instructions-number">1</span>
                <span>Получите QR-код регистрации от администратора</span>
              </li>
              <li className="onboarding-page__instructions-item">
                <span className="onboarding-page__instructions-number">2</span>
                <span>Нажмите "Начать сканирование"</span>
              </li>
              <li className="onboarding-page__instructions-item">
                <span className="onboarding-page__instructions-number">3</span>
                <span>Наведите камеру на QR-код</span>
              </li>
            </ul>
          </div>
        )}
        
        <div className="onboarding-page__card">
          <div className="onboarding-page__video-container">
            <video
              ref={videoRef}
              className="onboarding-page__video"
              playsInline
              muted
              style={{ display: scanning ? 'block' : 'none' }}
            />
            
            {/* Плейсхолдер когда камера не активна */}
            {!scanning && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                gap: '1rem'
              }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <span style={{ fontSize: '0.875rem' }}>Нажмите кнопку ниже</span>
              </div>
            )}
            
            {/* Оверлей сканирования с рамкой */}
            {scanning && (
              <div className="onboarding-page__scan-overlay">
                <div className="onboarding-page__scan-frame">
                  <div className="onboarding-page__scan-line" />
                </div>
                <div className="onboarding-page__scan-hint">
                  📷 Наведите на QR-код
                </div>
              </div>
            )}
          </div>
          
          <div className={`onboarding-page__status ${scanning ? 'onboarding-page__status--scanning' : ''} ${isProcessing ? 'onboarding-page__status--processing' : ''}`}>
            {isLoading && <span className="onboarding-page__loader" style={{ display: 'inline-block', marginRight: '0.5rem', verticalAlign: 'middle' }} />}
            {status}
          </div>
          
          {error && (
            <div className="onboarding-page__error">
              <strong>⚠️ Ошибка:</strong> {error}
            </div>
          )}
          
          {!scanning && !isProcessing ? (
            <button
              className="onboarding-page__button onboarding-page__button--primary"
              onClick={startScanning}
              disabled={isLoading}
            >
              📷 Начать сканирование
            </button>
          ) : scanning && !isProcessing ? (
            <button
              className="onboarding-page__button onboarding-page__button--secondary"
              onClick={stopScanning}
              disabled={isLoading}
            >
              ⏹ Остановить сканирование
            </button>
          ) : (
            <button
              className="onboarding-page__button"
              disabled
            >
              <span className="onboarding-page__loader" style={{ display: 'inline-block', marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Обработка...
            </button>
          )}
        </div>
      </div>
      
      <DebugLogger />
    </div>
  )
}

